import React, { useState, useEffect } from 'react'
import SchoolDisclosure from './SchoolDisclosure'

// Public demo-feedback survey at /feedback. No login, no Supabase client — the
// recruit-survey-submit edge function is the only network call this page makes,
// reached with the anon key because there is no session to borrow a token from.
//
// Built for a phone held in one hand immediately after a demo, which is why it
// is one question per screen rather than a scrolling form: a six-question form
// on a phone reads as long, and the person filling it in is standing up.
//
// NOTHING HERE IS AN ERROR WALL. The survey answers are saved by the function
// before it tries to match, claim or email, so every failure past that point is
// a follow-up problem rather than a lost response — and the result screens say
// so. The only screen that offers a retry is the one where the request itself
// did not land.
//
// THE LOOK follows the recruiting front page: navy canvas with a dot grid, and
// the survey itself sitting in a white panel wearing the same faux browser
// chrome that page puts around its product screenshots. The front page's own
// chrome CSS is scoped to .rec-root and is dark — #0C0C11 body, #101016 bar,
// traffic-light dots — so it is reproduced here in a light palette rather than
// imported. Same device-frame idea, different surface.

const NAVY = '#1d2e42'
const BLUE = '#147efb'
const GOLD = '#fdbf00'
const GOLD_INK = '#412402'
const DOT = '#33475f'
const MONO = "'Space Mono','SFMono-Regular',Menlo,monospace"

// Copied from src/App.js 4562-4616, path data untouched. App.js exports only
// its default component, so there is nothing to import — and redrawing these
// would put a second, drifting version of each tool's mark in the codebase.
// Defaults dropped: every caller here passes both props.
function StrategistIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 17 9 11 13 15 21 7"/><polyline points="15 7 21 7 21 13"/>
    </svg>
  )
}

function InspectionIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1z"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <polyline points="9 14 11 16 15 12"/>
    </svg>
  )
}

function AdaIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l9 4.5-9 4.5-9-4.5L12 3z"/>
      <path d="M5 10v4.5c0 2.5 3.1 4.5 7 4.5s7-2 7-4.5V10"/>
      <line x1="21" y1="7.5" x2="21" y2="13"/>
    </svg>
  )
}

function DealDeskIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="4" x2="12" y2="21"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="4" y1="7" x2="20" y2="7"/>
      <path d="M4 7L1.5 13h5z"/>
      <path d="M20 7L17.5 13h5z"/>
    </svg>
  )
}

function PipelineIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 4h18l-7 8v7l-4 2v-9L3 4z"/>
    </svg>
  )
}

// Field and action marks. Same drawing convention as the tool icons above —
// 24 viewBox, 2px stroke, currentColor — so a field icon and a rail icon are
// visibly the same hand.
function UserIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  )
}

function MailIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2"/><polyline points="3 7 12 13 21 7"/>
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"/>
    </svg>
  )
}

function ArrowRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/>
    </svg>
  )
}

function ArrowUpRightIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="7" y1="17" x2="17" y2="7"/><polyline points="9 7 17 7 17 15"/>
    </svg>
  )
}

// The app's own per-tool tints, copied from the rail's TOOLS array so the cards
// here and the tools in the Hub cannot drift apart. `Icon` is the component
// itself rather than a name, so a typo is a build error rather than a blank
// card.
const TOOLS = [
  { key: 'The Strategist',  line: 'Pricing and comps',      tile: '#E4EDF7', ink: '#123763', Icon: StrategistIcon },
  { key: 'Inspection desk', line: 'BINSR drafting',         tile: '#FAEEDA', ink: '#633806', Icon: InspectionIcon },
  { key: 'Deal desk',       line: 'Contracts and forms',    tile: '#EFE6F7', ink: '#46186B', Icon: DealDeskIcon },
  { key: 'Pipeline',        line: 'Your database, working', tile: '#DCEEEC', ink: '#0B4A44', Icon: PipelineIcon },
  { key: 'Ask ADA',         line: 'Arizona license law',    tile: '#EAF3DE', ink: '#27500A', Icon: AdaIcon },
]

const TOOLS_NOW = ['MLS tools only', 'Cloud CMA', 'RPR', 'Cromford', 'Something else', 'Nothing consistent']
const TIME_SINK = ['Pricing and CMAs', 'Contracts and forms', 'Inspection responses', 'Follow up and database', 'Keeping up with license law']
const WOULD_CHANGE = ['Not really', 'Somewhat', 'Definitely']
const OPT_IN = ['Yes, have someone reach out', 'Not right now']
const OPT_IN_YES = 'Yes, have someone reach out'

const REDEEM_STEPS = [
  'Go to avanceschool.com',
  'Create your account',
  'Pick your CE classes',
  'Enter the code at checkout',
]

// The broker call. Opened from the pre-submit window and from the code
// screen's link card, so it lives in one place.
const PATH_URL = 'https://tidycal.com/joshmarquez/the-path-to-100'

// Where somebody goes next after the code lands. New tab on all three: this
// page still holds the address their code was sent to, and navigating away from
// it in place loses that.
const NEXT_LINKS = [
  { label: 'See our tools', domain: 'truetopproducer.com',  href: 'https://truetopproducer.com' },
  { label: 'The Path to 100%', domain: '6 more CE hours', href: PATH_URL },
  { label: 'Avance School', domain: 'avanceschool.com',     href: 'https://avanceschool.com' },
]

const LAST_STEP = 6

export default function FeedbackSurvey() {
  const [step, setStep] = useState(0)
  const [identity, setIdentity] = useState({ name: '', email: '', phone: '' })
  const [answers, setAnswers] = useState({
    tools_now: '',
    which_tools: [],
    time_sink: '',
    missing: '',
    would_change: '',
    opt_in: '',
  })
  const [errors, setErrors] = useState({})
  const [stepError, setStepError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // 'sent' | 'notfound' | 'allset' | 'failed' — null until the request returns.
  const [result, setResult] = useState(null)

  // The Path to 100 window. Opens on "Get my code", closes into submit().
  const [showPath, setShowPath] = useState(false)

  // Once the window has been answered, a retry goes straight to submit.
  const [pathSeen, setPathSeen] = useState(false)

  // Lock the page behind the window so it can't scroll out from under
  // the scrim on touch. Restores whatever was there on close.
  useEffect(() => {
    if (!showPath) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [showPath])

  // Derived once and used by BOTH the POST body and the result screen, so what
  // the person is told the code went to is character-for-character what was
  // sent. Two separate .trim().toLowerCase() calls would be two things to keep
  // in step. The INPUT still shows exactly what they type — normalising under a
  // caret that is still moving is its own kind of wrong.
  // Name is deliberately NOT touched: "Danae O'Brien" is not "danae o'brien".
  const cleanEmail = identity.email.trim().toLowerCase()

  const setIdentityField = (field, value) => {
    setIdentity(prev => ({ ...prev, [field]: value }))
    // Cleared on edit, so an error never sits under a field the person has
    // already fixed.
    setErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev))
  }

  const setAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }))
    setStepError('')
  }

  const toggleTool = (name) => {
    setAnswers(prev => ({
      ...prev,
      which_tools: prev.which_tools.includes(name)
        ? prev.which_tools.filter(t => t !== name)
        : [...prev.which_tools, name],
    }))
    setStepError('')
  }

  function validateIdentity() {
    const next = {}
    if (!identity.name.trim()) next.name = 'Enter your full name'
    if (!identity.email.trim()) next.email = 'Enter your email'
    else if (!/^.+@.+\..+$/.test(identity.email.trim())) next.email = 'That does not look like an email address'
    if (!identity.phone.trim()) next.phone = 'Enter your phone number'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  // Question 4 is the only one that never blocks — it is the free-text box, and
  // "you can skip this" has to be true or it is a lie on the screen.
  function currentStepAnswered() {
    if (step === 1) return !!answers.tools_now
    if (step === 2) return answers.which_tools.length > 0
    if (step === 3) return !!answers.time_sink
    if (step === 4) return true
    if (step === 5) return !!answers.would_change
    if (step === 6) return !!answers.opt_in
    return true
  }

  function goNext() {
    if (step === 0) {
      if (!validateIdentity()) return
      setStep(1)
      return
    }
    if (!currentStepAnswered()) {
      setStepError('Pick an answer to keep going')
      return
    }
    if (step === LAST_STEP) {
      setStepError('')
      if (pathSeen) { submit(); return }
      setShowPath(true)
      return
    }
    setStepError('')
    setStep(s => s + 1)
  }

  function goBack() {
    setStepError('')
    setStep(s => Math.max(0, s - 1))
  }

  // Both exits from the window submit the survey. The only difference is
  // whether TidyCal opens first. Neither is stored — whether they booked
  // lives in TidyCal, not here.
  function pathBook() {
    window.open(PATH_URL, '_blank', 'noopener,noreferrer')
    setPathSeen(true)
    setShowPath(false)
    submit()
  }
  function pathSkip() {
    setPathSeen(true)
    setShowPath(false)
    submit()
  }

  async function submit() {
    // One submission per page load. The guard is on the same state the button
    // is disabled by, so a double tap that beats the re-render still lands here
    // and returns.
    if (submitting) return
    setSubmitting(true)
    setStepError('')
    try {
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/recruit-survey-submit`, {
        method: 'POST',
        headers: {
          // The anon key stands in for the session token every other caller in
          // this app sends — there is no signed-in user on a public page. The
          // function runs with JWT verification off and uses its own
          // service-role client, so it never reads this token as an identity.
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          name: identity.name.trim(),
          email: cleanEmail,
          phone: identity.phone.trim(),
          answers: {
            tools_now: answers.tools_now,
            which_tools: answers.which_tools,
            time_sink: answers.time_sink,
            missing: answers.missing.trim(),
            would_change: answers.would_change,
            opt_in: answers.opt_in,
          },
          opted_in: answers.opt_in === OPT_IN_YES,
        }),
      })

      if (!res.ok) {
        // The request itself did not land, so nothing is known to have been
        // saved. This is the only screen that offers a retry.
        console.error('[recruit-survey-submit] HTTP', res.status)
        setResult('failed')
        return
      }

      const data = await res.json().catch(() => ({}))

      if (data.code_sent === true) setResult('sent')
      else if (data.matched === false) setResult('notfound')
      // pool_empty, claim_failed and email_failed all mean the same thing to the
      // person on the phone: their answers are in and a human will finish it.
      // Anything else that came back 200 lands here too, because a 200 means the
      // row was saved and "you're all set" is then true whatever the flag says.
      else setResult('allset')
    } catch (err) {
      console.error('[recruit-survey-submit] threw:', err)
      setResult('failed')
    } finally {
      setSubmitting(false)
    }
  }

  // Returns to screen 0 with everything they typed still there — asking somebody
  // to retype a name that did not match is how you lose the second attempt too.
  function tryAgain() {
    setResult(null)
    setErrors({})
    setStepError('')
    setStep(0)
  }

  // The top-right status label. Reads the same state the screens do, so it
  // cannot describe a step the page is not on.
  const onQuestion = !result && step > 0
  const barLabel = result
    ? 'FEEDBACK · DONE'
    : onQuestion
      ? `FEEDBACK · QUESTION ${step} OF ${LAST_STEP}`
      : 'FEEDBACK · 2 MIN'

  return (
    <>
      <style>{CSS}</style>
      <div className="fb-page">
        <header className="fb-bar">
          <div className="fb-brand">
            <span className="fb-mark">&#9654;</span>
            <span className="fb-word">HOMEPROS </span>
            <span className="fb-word fb-word-blue">HUB</span>
          </div>
          <span className="fb-status">{barLabel}</span>
        </header>

        <main className="fb-main">
          <div className="fb-panel">
            {/* Same device frame the front page puts around its screenshots,
                in a light palette. Decorative: the dots are not controls and
                the URL is not a link. */}
            <div className="fb-chrome" aria-hidden="true">
              <span className="fb-dot" /><span className="fb-dot" /><span className="fb-dot" />
              <div className="fb-url">truetopproducer.com/feedback</div>
            </div>

            {onQuestion && (
              <div
                className="fb-seg"
                role="progressbar"
                aria-valuenow={step}
                aria-valuemin={1}
                aria-valuemax={LAST_STEP}
                aria-label={`Question ${step} of ${LAST_STEP}`}
              >
                {Array.from({ length: LAST_STEP }, (_, i) => (
                  <span key={i} className={`fb-seg-i ${i < step ? 'is-on' : ''}`} />
                ))}
              </div>
            )}

            <div className="fb-body-wrap">
              {result === 'sent' && (
                <div className="fb-screen">
                  <Eyebrow>Done</Eyebrow>
                  <div className="fb-done-head">
                    <span className="fb-check" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <h1 className="fb-h1 fb-h1-inline">Check your email.</h1>
                  </div>
                  <p className="fb-body">
                    Your code is on its way to {cleanEmail}. It's good for two CE classes
                    at Avance School — 100% off, no card needed.
                  </p>
                  <div className="fb-redeem">
                    <div className="fb-redeem-title">How to redeem</div>
                    <ol className="fb-redeem-list">
                      {REDEEM_STEPS.map(s => <li key={s}>{s}</li>)}
                    </ol>
                  </div>

                  <div className="fb-rule" />

                  <Eyebrow>What you saw today</Eyebrow>
                  <div className="fb-chips">
                    {TOOLS.map(t => (
                      <span key={t.key} className="fb-chip" style={{ background: t.tile, color: t.ink }}>
                        <t.Icon size={13} color={t.ink} />
                        {t.key}
                      </span>
                    ))}
                  </div>

                  <div className="fb-links">
                    {NEXT_LINKS.map(l => (
                      <a
                        key={l.href}
                        className="fb-linkcard"
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="fb-linkcard-text">
                          <span className="fb-linkcard-label">{l.label}</span>
                          <span className="fb-linkcard-domain">{l.domain}</span>
                        </span>
                        <span className="fb-linkcard-arrow" aria-hidden="true"><ArrowUpRightIcon /></span>
                      </a>
                    ))}
                  </div>

                  {answers.opt_in === OPT_IN_YES && (
                    <p className="fb-body fb-body-tight">Danae will follow up this week.</p>
                  )}
                </div>
              )}

              {result === 'notfound' && (
                <div className="fb-screen">
                  <Eyebrow>Not found</Eyebrow>
                  <h1 className="fb-h1">We couldn't find you.</h1>
                  <p className="fb-body">
                    Check that the name, email, and phone match what you gave Danae, or reach out to
                    her directly — your answers were saved either way.
                  </p>
                  <button type="button" className="fb-link fb-link-standalone" onClick={tryAgain}>
                    Try again
                  </button>
                  <p className="fb-body fb-body-tight">
                    Or <a className="fb-path-link" href={PATH_URL} target="_blank" rel="noopener noreferrer">book the broker call →</a>
                  </p>
                </div>
              )}

              {result === 'allset' && (
                <div className="fb-screen">
                  <Eyebrow>All set</Eyebrow>
                  <div className="fb-done-head">
                    <span className="fb-check" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <h1 className="fb-h1 fb-h1-inline">You're all set.</h1>
                  </div>
                  <p className="fb-body">Your answers are in. Your code is on its way by email shortly.</p>

                  <div className="fb-links">
                    {NEXT_LINKS.map(l => (
                      <a
                        key={l.href}
                        className="fb-linkcard"
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="fb-linkcard-text">
                          <span className="fb-linkcard-label">{l.label}</span>
                          <span className="fb-linkcard-domain">{l.domain}</span>
                        </span>
                        <span className="fb-linkcard-arrow" aria-hidden="true"><ArrowUpRightIcon /></span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {result === 'failed' && (
                <div className="fb-screen">
                  <Eyebrow>Error</Eyebrow>
                  <h1 className="fb-h1">Something went wrong.</h1>
                  <p className="fb-body">Give it a minute and try again.</p>
                  <button
                    type="button"
                    className="fb-next"
                    onClick={() => { setResult(null); submit() }}
                    disabled={submitting}
                  >
                    {submitting ? <span className="fb-spinner" aria-hidden="true" /> : null}
                    {submitting ? 'Sending…' : 'Try again'}
                  </button>
                </div>
              )}

              {!result && step === 0 && (
                <div className="fb-screen">
                  <Eyebrow>Welcome</Eyebrow>
                  <h1 className="fb-h1">
                    You just saw the brain of a broker
                    <span className="fb-h1-alt">at the speed of AI.</span>
                  </h1>
                  <p className="fb-body">
                    Six quick questions. Your answers go straight to the people building this, and
                    your two free CE classes go straight to your inbox.
                  </p>
                  <div className="fb-fields">
                    <Field
                      label="Full name" value={identity.name} error={errors.name}
                      onChange={v => setIdentityField('name', v)}
                      autoComplete="name"
                      icon={<UserIcon />}
                    />
                    {/* A phone keyboard will capitalise and autocorrect an address
                        for you if you let it, and "Broker@X.com" typed on a phone is
                        how a match against a lowercased FUB record fails. */}
                    <Field
                      label="Email" value={identity.email} error={errors.email}
                      onChange={v => setIdentityField('email', v)}
                      type="email" inputMode="email" autoComplete="email"
                      autoCapitalize="none" autoCorrect="off" spellCheck={false}
                      icon={<MailIcon />}
                    />
                    <Field
                      label="Phone" value={identity.phone} error={errors.phone}
                      onChange={v => setIdentityField('phone', v)}
                      type="tel" inputMode="tel" autoComplete="tel"
                      icon={<PhoneIcon />}
                    />
                  </div>
                  <p className="fb-fine">
                    Use the same name, email, and phone you gave Danae. If they don't match we can't
                    find you.
                  </p>
                </div>
              )}

              {!result && step === 1 && (
                <Question
                  step={step}
                  heading="What are you using today for pricing and comps?"
                  sub="Tap one."
                  error={stepError}
                >
                  {TOOLS_NOW.map(o => (
                    <Choice key={o} label={o} selected={answers.tools_now === o} onSelect={() => setAnswer('tools_now', o)} />
                  ))}
                </Question>
              )}

              {!result && step === 2 && (
                <Question
                  step={step}
                  heading="Which of the tools we just showed you would you actually use?"
                  sub="Tap every one you'd use. Tap again to unselect."
                  error={stepError}
                >
                  {TOOLS.map(t => {
                    const on = answers.which_tools.includes(t.key)
                    return (
                      <button
                        key={t.key}
                        type="button"
                        className={`fb-tool ${on ? 'is-on' : ''}`}
                        style={{ background: t.tile }}
                        aria-pressed={on}
                        onClick={() => toggleTool(t.key)}
                      >
                        <span className="fb-tool-ic" style={{ color: t.ink }}>
                          <t.Icon size={18} color={t.ink} />
                        </span>
                        <span className="fb-tool-name" style={{ color: t.ink }}>{t.key}</span>
                        <span className="fb-tool-line" style={{ color: t.ink }}>{t.line}</span>
                        {on && (
                          <span className="fb-tick" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                        )}
                      </button>
                    )
                  })}
                </Question>
              )}

              {!result && step === 3 && (
                <Question
                  step={step}
                  heading="What part of your week takes the most time?"
                  sub="Tap one."
                  error={stepError}
                >
                  {TIME_SINK.map(o => (
                    <Choice key={o} label={o} selected={answers.time_sink === o} onSelect={() => setAnswer('time_sink', o)} />
                  ))}
                </Question>
              )}

              {!result && step === 4 && (
                <Question
                  step={step}
                  heading="What felt missing or unclear?"
                  sub="Type anything. You can skip this."
                  error=""
                >
                  <textarea
                    className="fb-textarea"
                    value={answers.missing}
                    onChange={e => setAnswer('missing', e.target.value)}
                    rows={5}
                    placeholder="Anything at all"
                  />
                </Question>
              )}

              {!result && step === 5 && (
                <Question
                  step={step}
                  heading="If your brokerage handed you this tomorrow, would it change how you work?"
                  sub="Tap one."
                  error={stepError}
                >
                  {WOULD_CHANGE.map(o => (
                    <Choice key={o} label={o} selected={answers.would_change === o} onSelect={() => setAnswer('would_change', o)} />
                  ))}
                </Question>
              )}

              {!result && step === 6 && (
                <Question
                  step={step}
                  heading="Would you like to hear more about HomePros and how we're building this?"
                  sub="Tap one. You get your free classes either way."
                  error={stepError}
                >
                  {OPT_IN.map(o => (
                    <Choice key={o} label={o} selected={answers.opt_in === o} onSelect={() => setAnswer('opt_in', o)} />
                  ))}
                </Question>
              )}

              {!result && (
                <div className="fb-actions">
                  <button type="button" className="fb-next" onClick={goNext} disabled={submitting}>
                    {submitting ? <span className="fb-spinner" aria-hidden="true" /> : null}
                    {step === 0 ? 'Start' : step === LAST_STEP ? (submitting ? 'Sending…' : 'Get my code') : 'Next'}
                    {!submitting && <ArrowRightIcon />}
                  </button>
                  {/* From question 2 on, per the brief — question 1 has nothing
                      behind it worth going back to mid-survey. */}
                  {step >= 2 && (
                    <button type="button" className="fb-link" onClick={goBack} disabled={submitting}>
                      Back
                    </button>
                  )}
                </div>
              )}

              {/* School identification. Last child of .fb-body-wrap, which is
                  the only element inside the panel with no render guard — every
                  screen, question and result alike, is a conditional child of
                  it — so this is on screen throughout rather than only on the
                  one screen that hands out the code. 26px matches the wrapper's
                  own padding and .fb-actions' margin-top, the two values that
                  set this container's rhythm. No bottom margin: .fb-body-wrap
                  already closes with 26px of padding (20px under 599px). */}
              <div style={{marginTop:'26px'}}>
                <SchoolDisclosure />
              </div>
            </div>
          </div>
        </main>

        {showPath && (
          <div className="fb-path-scrim" role="dialog" aria-modal="true" aria-labelledby="fb-path-title">
            <div className="fb-path">
              <div className="fb-path-head">
                <div className="fb-path-eyebrow">Before you grab your code</div>
                <h2 id="fb-path-title" className="fb-path-title">We don't hand out 100%.<br />We build agents who earn it.</h2>
              </div>
              <div className="fb-path-body">
                <p>HomePros isn't a 100% brokerage. We're a producing-producers brokerage: we take agents, give them the tools you just saw, build them through weekly Lead Lab, and when they hit the production criteria, they move to the Performance 100% structure.</p>
                <p>Twenty minutes with the broker will tell you where you'd start and what it takes to get there. <b>Six more CE hours</b> for taking the call.</p>
                <div className="fb-path-actions">
                  <button type="button" className="fb-next" onClick={pathBook} disabled={submitting}>Book the broker call</button>
                  <button type="button" className="fb-link" onClick={pathSkip} disabled={submitting}>Not now, just my code</button>
                </div>
                <div className="fb-path-note">Your survey still submits either way.</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// Every screen opens with one of these, so the panel always says where you are
// even with the segments hidden.
function Eyebrow({ children }) {
  return <div className="fb-eyebrow">{children}</div>
}

function Field({ label, value, onChange, error, type = 'text', inputMode, autoComplete, autoCapitalize, autoCorrect, spellCheck, icon }) {
  return (
    <label className="fb-field">
      <span className="fb-label">{label}</span>
      <span className="fb-input-wrap">
        {icon && <span className="fb-input-ic" aria-hidden="true">{icon}</span>}
        <input
          className={`fb-input ${error ? 'is-bad' : ''}`}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          autoCapitalize={autoCapitalize}
          autoCorrect={autoCorrect}
          spellCheck={spellCheck}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      </span>
      {error && <span className="fb-err">{error}</span>}
    </label>
  )
}

// Zero-padded on both sides — "QUESTION 02 / 06" holds its width as the number
// climbs, so the label does not shuffle between screens.
function Question({ step, heading, sub, error, children }) {
  return (
    <div className="fb-screen">
      <Eyebrow>{`Question ${String(step).padStart(2, '0')} / ${String(LAST_STEP).padStart(2, '0')}`}</Eyebrow>
      <h1 className="fb-h1 fb-h1-q">{heading}</h1>
      <p className="fb-sub">{sub}</p>
      <div className="fb-choices">{children}</div>
      {error && <p className="fb-err fb-err-step">{error}</p>}
    </div>
  )
}

function Choice({ label, selected, onSelect }) {
  return (
    <button
      type="button"
      className={`fb-choice ${selected ? 'is-on' : ''}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      {label}
    </button>
  )
}

const CSS = `
  .fb-page {
    min-height: 100vh;
    min-height: 100dvh;
    background-color: ${NAVY};
    background-image: radial-gradient(${DOT} 1px, transparent 1px);
    background-size: 22px 22px;
    font-family: 'Barlow', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    display: flex;
    flex-direction: column;
    color: ${NAVY};
  }
  .fb-page * { box-sizing: border-box; }

  .fb-bar {
    flex-shrink: 0;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .fb-brand { display: flex; align-items: center; min-width: 0; }
  .fb-mark { color: ${GOLD}; font-size: 17px; margin-right: 9px; line-height: 1; }
  .fb-word {
    color: #ffffff; font-weight: 900; font-size: 15px; letter-spacing: 2px;
    font-family: 'Barlow Condensed', sans-serif;
  }
  .fb-word-blue { color: ${BLUE}; }
  .fb-status {
    font-family: ${MONO}; font-size: 10.5px; letter-spacing: 0.08em;
    color: #8fa3bb; white-space: nowrap; flex-shrink: 0;
  }

  .fb-main {
    flex: 1;
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 12px 16px 44px;
  }

  .fb-panel {
    width: 100%;
    max-width: 560px;
    align-self: flex-start;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(0,0,0,.32);
  }

  /* Device frame, light palette — see the note at the top of this file. */
  .fb-chrome {
    display: flex; align-items: center; gap: 7px;
    padding: 10px 13px;
    background: #f4f4f2;
    border-bottom: 1px solid #e4e4e2;
  }
  .fb-dot { width: 9px; height: 9px; border-radius: 50%; background: #c9c9c4; flex: none; }
  .fb-url {
    flex: 1; min-width: 0; text-align: center;
    font-family: ${MONO}; font-size: 10.5px; color: #9a9a94;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    padding-right: 34px;
  }

  .fb-seg { display: flex; gap: 4px; padding: 14px 26px 0; }
  .fb-seg-i {
    flex: 1; height: 4px; border-radius: 2px; background: #e8e8e4;
    transition: background .25s ease;
  }
  .fb-seg-i.is-on { background: ${BLUE}; }

  .fb-body-wrap { padding: 26px; display: flex; flex-direction: column; }
  .fb-screen { flex: 1; }

  .fb-eyebrow {
    font-family: ${MONO}; font-size: 10px; letter-spacing: 1.5px;
    text-transform: uppercase; color: #8a8a84; margin: 0 0 12px;
  }

  .fb-h1 {
    font-size: 27px; line-height: 1.15; font-weight: 700; color: ${NAVY};
    margin: 0 0 12px; font-family: 'Barlow Condensed', sans-serif; letter-spacing: 0.3px;
  }
  .fb-h1-alt { display: block; color: ${BLUE}; }
  .fb-h1-q { font-size: 24px; }
  .fb-h1-inline { margin: 0; }
  .fb-body { font-size: 15px; line-height: 1.6; color: #555; margin: 0 0 22px; }
  .fb-body-tight { margin: 18px 0 0; }
  .fb-sub { font-size: 13px; color: #888; margin: 0 0 18px; }
  .fb-fine { font-size: 12.5px; line-height: 1.5; color: #888; margin: 18px 0 0; }

  .fb-fields { display: flex; flex-direction: column; gap: 14px; }
  .fb-field { display: block; }
  .fb-label {
    display: block; font-size: 10px; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: #999; margin-bottom: 6px;
    font-family: 'Barlow Condensed', sans-serif;
  }
  .fb-input-wrap { position: relative; display: block; }
  .fb-input-ic {
    position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
    color: #a8a8a2; display: flex; pointer-events: none;
  }
  .fb-input {
    width: 100%; background: #ffffff; border: 1px solid #e0e0e0; border-radius: 10px;
    padding: 13px 14px 13px 40px; font-size: 16px; font-family: inherit; color: ${NAVY};
    outline: none; transition: border-color .15s;
  }
  .fb-input:focus { border-color: ${BLUE}; }
  .fb-input:focus ~ .fb-input-ic { color: ${BLUE}; }
  .fb-input.is-bad { border-color: #C8102E; }
  .fb-err { display: block; color: #C8102E; font-size: 12.5px; margin-top: 6px; }
  .fb-err-step { margin: 14px 0 0; }

  .fb-choices { display: flex; flex-direction: column; gap: 9px; }

  .fb-choice {
    width: 100%; text-align: left; background: #ffffff;
    border: 2px solid #e8e8e4; border-radius: 12px;
    padding: 14px 15px; font-size: 15px; font-family: inherit; color: ${NAVY};
    cursor: pointer; transition: border-color .15s, background .15s;
  }
  .fb-choice.is-on { border-color: ${BLUE}; background: rgba(20,126,251,0.06); }

  .fb-tool {
    position: relative;
    width: 100%; text-align: left; border: 2px solid transparent; border-radius: 12px;
    padding: 14px 15px; font-family: inherit; cursor: pointer;
    display: flex; flex-direction: column; gap: 3px; transition: border-color .15s;
  }
  .fb-tool.is-on { border-color: ${BLUE}; }
  .fb-tool-ic { display: flex; margin-bottom: 5px; }
  .fb-tool-name { font-size: 15px; font-weight: 600; padding-right: 26px; }
  .fb-tool-line { font-size: 13px; opacity: 0.78; }
  .fb-tick {
    position: absolute; top: 12px; right: 12px;
    width: 18px; height: 18px; border-radius: 50%; background: ${BLUE};
    display: flex; align-items: center; justify-content: center;
  }
  .fb-tick svg { width: 11px; height: 11px; }

  .fb-textarea {
    width: 100%; background: #ffffff; border: 1px solid #e0e0e0; border-radius: 12px;
    padding: 14px; font-size: 16px; font-family: inherit; color: ${NAVY};
    outline: none; resize: vertical; line-height: 1.5;
  }
  .fb-textarea:focus { border-color: ${BLUE}; }

  .fb-actions {
    flex-shrink: 0; margin-top: 26px;
    display: flex; flex-direction: column; align-items: center; gap: 14px;
  }
  .fb-next {
    width: 100%; background: ${GOLD}; color: ${GOLD_INK};
    border: none; border-radius: 10px; padding: 15px;
    font-size: 15px; font-weight: 600; font-family: inherit; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 9px;
    transition: opacity .15s;
  }
  .fb-next:disabled { opacity: 0.6; cursor: default; }

  .fb-link {
    background: none; border: none; padding: 4px; font-size: 13.5px;
    font-family: inherit; color: #888; cursor: pointer;
  }
  .fb-link:disabled { opacity: 0.5; cursor: default; }
  .fb-link-standalone { color: ${BLUE}; padding: 0; margin-top: 18px; font-size: 15px; }

  .fb-path-scrim {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(13, 22, 34, 0.72);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
  }
  .fb-path {
    width: 100%; max-width: 440px; background: #ffffff; border-radius: 14px;
    overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,.4);
  }
  .fb-path-head { background: ${NAVY}; padding: 18px 24px 16px; }
  .fb-path-eyebrow {
    font-family: ${MONO}; font-size: 10px; letter-spacing: 1.5px;
    text-transform: uppercase; color: ${GOLD}; margin: 0 0 8px;
  }
  .fb-path-title {
    font-size: 21px; line-height: 1.2; font-weight: 700; color: #ffffff; margin: 0;
    font-family: 'Barlow Condensed', sans-serif; letter-spacing: 0.3px;
  }
  .fb-path-body { padding: 18px 24px 22px; }
  .fb-path-body p { font-size: 14px; line-height: 1.65; color: ${NAVY}; margin: 0 0 12px; }
  .fb-path-body b { font-weight: 600; }
  .fb-path-actions { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-top: 18px; }
  .fb-path .fb-next { width: auto; min-width: 0; align-self: auto; padding: 12px 22px; border-radius: 999px; font-size: 14px; }
  .fb-path-note { font-size: 11.5px; color: #999; margin-top: 14px; }
  .fb-path-link { color: ${BLUE}; font-weight: 600; text-decoration: none; }

  .fb-spinner {
    width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0;
    border: 2px solid rgba(65,36,2,0.25); border-top-color: ${GOLD_INK};
    animation: fb-spin .7s linear infinite;
  }
  @keyframes fb-spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .fb-spinner { animation: none; }
    .fb-seg-i { transition: none; }
  }

  .fb-done-head { display: flex; align-items: center; gap: 13px; margin: 0 0 14px; }
  .fb-check {
    width: 38px; height: 38px; border-radius: 50%; background: #2d7a2d; flex: none;
    display: flex; align-items: center; justify-content: center;
  }
  .fb-check svg { width: 20px; height: 20px; }

  .fb-redeem {
    background: #f0f0ef; border: 1px solid #e4e4e2; border-radius: 12px;
    padding: 16px 18px; margin-top: 4px;
  }
  .fb-redeem-title {
    font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    color: #888; margin-bottom: 10px; font-family: 'Barlow Condensed', sans-serif;
  }
  .fb-redeem-list { margin: 0; padding-left: 20px; color: #555; font-size: 14.5px; line-height: 1.9; }

  .fb-rule { height: 1px; background: #e8e8e4; margin: 26px 0 22px; }

  .fb-chips { display: flex; flex-wrap: wrap; gap: 7px; }
  .fb-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 11px; border-radius: 20px;
    font-size: 12.5px; font-weight: 600; white-space: nowrap;
  }

  .fb-links { display: grid; grid-template-columns: 1fr; gap: 9px; margin-top: 22px; }
  .fb-linkcard {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    min-width: 0;
    border: 1px solid #e8e8e4; border-radius: 12px; padding: 13px 15px;
    text-decoration: none; color: ${NAVY};
    transition: border-color .15s, background .15s;
  }
  .fb-linkcard:hover { border-color: ${BLUE}; background: rgba(20,126,251,0.04); }
  .fb-linkcard-text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .fb-linkcard-label { font-size: 14px; font-weight: 600; }
  .fb-linkcard-domain {
    font-family: ${MONO}; font-size: 10.5px; color: #9a9a94;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .fb-linkcard-arrow { color: #b4b4ae; display: flex; flex: none; }
  .fb-linkcard:hover .fb-linkcard-arrow { color: ${BLUE}; }

  @media (max-width: 599px) {
    .fb-main { padding: 10px 16px 36px; }
    .fb-body-wrap { padding: 20px; }
    .fb-seg { padding: 13px 20px 0; }
    .fb-h1 { font-size: 24px; }
    .fb-h1-q { font-size: 21px; }
    .fb-bar { padding: 14px 16px; }
    .fb-status { font-size: 9.5px; letter-spacing: 0.06em; }
  }

  @media (min-width: 600px) {
    .fb-main { padding: 20px 24px 56px; }
    .fb-links { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .fb-next { width: auto; min-width: 200px; align-self: flex-start; }
    .fb-actions { align-items: flex-start; flex-direction: row; gap: 20px; }
  }
`
